import { Injectable } from '@angular/core';
import { Calendar, durationStringToMinutes, MeetingType } from '@vendasta/meetings';
import {
  combineLatest,
  distinctUntilChanged,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { GROUP, SERVICE } from '../../constants';
import { MeetingListSharedDataService } from '../shared/shared-table-view.service';
import { GroupMeetingType, MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { TranslateService } from '@ngx-translate/core';
import isEqual from 'lodash/isEqual';

@Injectable()
export class MeetingListLoadDataService {
  private unsubscribe$ = new Subject<void>();
  groupMeetingTypes$: Observable<{ [calendarId: string]: GroupMeetingType[] }>;
  personalCalendar$: Observable<Calendar>;
  personalMeetingTypes$: Observable<MeetingType[]>;
  groupCalendars$: Observable<Calendar[]>;
  allGroupMeetingTypes$: Observable<MeetingType[]>;
  allGroupCalendarIds$: Observable<string[]>;
  constructor(
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private sharedTableService: MeetingListSharedDataService,
    private sharedDataService: MeetingListSharedDataService,
    private readonly translate: TranslateService,
  ) {}

  getDefaultGroup(): any[] {
    const personalDefaultCalendar: Calendar = this.sharedTableService.getPersonalDefaultCalendar();
    const teamDefaultCalendarLink: Calendar[] = this.sharedTableService.getGroupCalendar();
    const personalMeetingTypes = this.sharedTableService.getPersonalMeetingTypes();
    let defaultCalendar = [];
    if (personalMeetingTypes.length > 0) {
      defaultCalendar = [
        {
          id: personalDefaultCalendar?.id,
          name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_PERSONAL_GROUP.NAME'),
          description: this.translate.instant(
            'MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_PERSONAL_GROUP.DESCRIPTION',
          ),
          isPinned: true,
          associations: this.sharedTableService.getPersonalMeetingTypes(),
          bookingUrl: personalDefaultCalendar?.bookingUrl,
          isDefaultGroup: true,
        },
      ];
    }
    teamDefaultCalendarLink.forEach((calendar: Calendar) => {
      const groupMeetingTypes = this.sharedTableService.getGroupMeetingTypes()[calendar?.id];
      if (groupMeetingTypes.length > 0) {
        defaultCalendar.push({
          id: calendar?.id,
          name: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_TEAM_GROUP.NAME', {
            teamName: calendar?.displayName,
          }),
          description: this.translate.instant(
            'MEETING_SCHEDULER.MEETING_LIST.TABLE.VIEW.DEFAULT_TEAM_GROUP.DESCRIPTION',
            {
              teamName: calendar?.displayName,
            },
          ),
          isPinned: true,
          associations: groupMeetingTypes,
          bookingUrl: calendar?.bookingUrl,
          isDefaultGroup: true,
        });
      }
    });
    return defaultCalendar;
  }

  getEventTypeData() {
    this.personalMeetingTypes$ = this.meetingSchedulerStoreService.loadPersonalMeetingTypes();
    this.personalCalendar$ = this.meetingSchedulerStoreService.loadPersonalCalendar();
    this.sharedDataService.setPersonalDefaultCalendar(this.personalCalendar$);
    this.sharedDataService.setPersonalMeetingTypes(this.personalMeetingTypes$);
    this.groupCalendars$ = this.meetingSchedulerStoreService.loadGroupCalendars();
    this.allGroupCalendarIds$ = this.groupCalendars$.pipe(
      map((calendars) => calendars?.map((calendar) => calendar.id) || []),
      startWith([]),
    );

    this.allGroupCalendarIds$
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(
          (calendarIds) =>
            calendarIds.length
              ? forkJoin(
                  calendarIds.map((calendarId) => {
                    return this.meetingSchedulerStoreService.getGroupCalendarDataFromState(calendarId)
                      ? of(null)
                      : this.meetingSchedulerStoreService.loadCalendar(calendarId);
                  }),
                )
              : of([]), // Prevent empty `forkJoin`
        ),
      )
      .subscribe();
    this.sharedDataService.setGroupCalendar(this.groupCalendars$);
    this.groupMeetingTypes$ = this.allGroupCalendarIds$.pipe(
      distinctUntilChanged((prev, curr) => isEqual(prev, curr)), // Prevents duplicate emissions
      switchMap((ids) =>
        ids.length > 0 ? this.meetingSchedulerStoreService.loadGroupMeetingTypes({ returnCache: true }) : of({}),
      ),
      shareReplay(1),
    );
    this.sharedDataService.setGroupMeetingTypes(this.groupMeetingTypes$);

    this.allGroupMeetingTypes$ = this.groupMeetingTypes$.pipe(
      map((groupMeetingTypes) =>
        Object.keys(groupMeetingTypes || {}).reduce(
          (acc, calendarId) => acc.concat(groupMeetingTypes[calendarId] || []),
          [] as MeetingType[],
        ),
      ),
      startWith([]),
      shareReplay(1),
    );
    return combineLatest([this.personalMeetingTypes$, this.allGroupMeetingTypes$]).pipe(
      map(([personalMeetings, groupMeetings]) => [
        ...personalMeetings.map((meeting) => ({
          ...meeting,
          duration: `${this.toMinutes(meeting.duration)} mins`,
          eventType: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.PERSONAL_EVENT'),
        })),
        ...groupMeetings.map((meeting) => ({
          ...meeting,
          duration: `${this.toMinutes(meeting.duration)} mins`,
          eventType: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.TEAM_EVENT'),
          teamName: meeting.teamName,
        })),
      ]),
      shareReplay(1),
    );
  }

  toMinutes(duration: string): number {
    return durationStringToMinutes({ duration });
  }

  loadGroupOrServiceData(selectedToggle): Observable<any[]> {
    let data$: Observable<any[]>;
    switch (selectedToggle) {
      case GROUP:
        data$ = this.meetingSchedulerStoreService.loadGroups();
        break;
      case SERVICE:
        data$ = this.meetingSchedulerStoreService.loadServices();
        break;
      default:
        data$ = this.getEventTypeData();
    }
    return this.sortData(data$, selectedToggle);
  }

  sortData(data$, selectedToggle) {
    return data$.pipe(
      map((data) => {
        if (Array.isArray(data)) {
          data.sort((a, b) => {
            if (a.isPinned !== b.isPinned) {
              return Number(b.isPinned) - Number(a.isPinned);
            }
            const nameA = a.name ? a.name.toLowerCase() : '';
            const nameB = b.name ? b.name.toLowerCase() : '';

            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          });

          if (selectedToggle === GROUP) {
            return [...this.getDefaultGroup(), ...data];
          } else {
            return data;
          }
        }
        return [];
      }),
    );
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
